import { createForm } from '@seek/forms-ui';
import { required } from '@seek/validators-js';
import { useTranslations } from '@vocab/react';
import {
  Alert,
  Button,
  Stack,
  Text,
  TextLink,
  ButtonLink,
} from 'braid-design-system';

import forgotPassword from '../../../../modules/Auth0/forgotPassword';
import type { FriendlyForgotPasswordError } from '../../../../modules/Auth0/types';
import { useAnalyticsContext } from '../../../../modules/analytics/AnalyticsContext';
import { Experience, type FormProps } from '../../../../types';
import getHashRoute from '../../../../utils/getHashRoute';
import { handleFormSubmit } from '../../../../utils/handleFormSubmit';
import { getDeepLink } from '../../../../utils/mobile/forMobile';
import { useAppContext } from '../../../AppContext';
import useTrackError from '../../../hooks/analytics/useTrackError';
import useTrackPageView from '../../../hooks/analytics/useTrackPageView';
import useFormLoadMetrics from '../../../hooks/metrics/useFormLoadMetrics';
import useContextEmail from '../../../hooks/useContextEmail';
import useFormState from '../../../hooks/useFormState';
import FormCard from '../../../shared/FormCard/FormCard';
import FormHeader from '../../../shared/FormHeader/FormHeader';
import FormStack from '../../../shared/FormStack';
import Nudge from '../../../shared/Nudge/Nudge';
import ServerError from '../../../shared/ServerError/ServerError';
import TertiaryLinks from '../../../shared/TertiaryLinks';
import TrackLink from '../../../shared/TrackLink/TrackLink';
import { useLoginPageContext } from '../LoginPageContext';
import { LOGIN } from '../hashRoutes';
import EmailField from '../shared/components/EmailField/EmailField';

import translations from './.vocab';

interface FormValues {
  emailAddress: string;
}

const formDetails = {
  FORGOT_PASSWORD: {
    pageName: 'forgot password',
    formName: 'forgot_password',
  },
  EXPIRED_PASSWORD: {
    pageName: 'expired password',
    formName: 'expired_password',
  },
} as const;

const { FormProvider, useField } = createForm<FormValues>();

const Form = ({ handleSubmit }: FormProps<FormValues>) => {
  const { t } = useTranslations(translations);
  const {
    setEmailAddress,
    realm: realmOverride,
    loginMessage,
  } = useLoginPageContext();
  const {
    clientContactPath,
    experience,
    isMobileClient,
    getClientHref,
    realm,
  } = useAppContext();
  const [formState, setFormState] = useFormState<FriendlyForgotPasswordError>();
  const emailAddress = useField({ id: 'emailAddress', validators: [required] });

  const isExpiredPasswordForm = loginMessage === 'error.expired_password';
  const { pageName, formName } =
    formDetails[isExpiredPasswordForm ? 'EXPIRED_PASSWORD' : 'FORGOT_PASSWORD'];

  useFormLoadMetrics(formName);
  useContextEmail(emailAddress.value, setEmailAddress);
  const { loading, submitted, error } = formState;
  useTrackPageView(
    submitted
      ? `${formName}_form_submit_succeeded`
      : `${formName}_form_displayed`,
    pageName,
  );

  useTrackError({ error });
  const { analytics } = useAnalyticsContext();
  const { trackLink } = analytics;

  const onSubmit = handleSubmit(async (formValues) => {
    handleFormSubmit(
      new Promise<void>(async (resolve) => {
        resolve();
      }).then(() =>
        forgotPassword(analytics, {
          ...formValues,
          connection: realmOverride || realm,
        }),
      ),
      setFormState,
      formName,
    );
  });

  const pageContentTranslated = {
    instructionSent: t(
      'If email is registered, we’ve sent you instructions on how to reset your password.',
      { email: emailAddress.value },
    ),
    submittedText: t(
      'You should receive the email fairly quickly. If you can’t find it, try checking your spam folder.',
    ),
    formPrimaryText: isExpiredPasswordForm
      ? t(
          'Your password has expired. Enter your email address below and we will send you a link to reset your password.',
        )
      : t(
          'Enter your email address below and we will send you a link to reset your password.',
        ),
    additionalHelpTertiaryLink: t(
      'If you need additional help, please contact customer service',
      {
        CustomerServiceLink: (v) => (
          <TrackLink
            key="1"
            href={getClientHref(clientContactPath ?? '/contactus/')}
            trackingData={{
              eventName: 'customer_service_pressed',
              linkPosition: 'prompt',
              isThirdPartyLink: 'false',
            }}
          >
            {v}
          </TrackLink>
        ),
      },
    ),
    advertiserCentreTertiaryLink: t(
      'Trying to sign in as an employer or recruiter? Advertiser centre help',
      {
        br: () => <br />,
        AdCentreLink: (v) => (
          <TextLink
            key="link"
            weight="regular"
            href={getClientHref('/contactus/', Experience.Hirer)}
          >
            {v}
          </TextLink>
        ),
      },
    ),
    signIn: t('Sign in'),
    continue: t('Continue'),
    next: t('Next'),
  };

  return (
    <Stack space="none">
      <Nudge experience />
      <FormCard>
        <FormHeader
          title={isExpiredPasswordForm ? 'Expired password' : 'Forgot password'}
        />
        {submitted ? (
          <Stack space="small">
            <Stack space="large">
              <Alert tone="positive">
                <Text>{pageContentTranslated.instructionSent}</Text>
              </Alert>
              <Text>{pageContentTranslated.submittedText}</Text>
            </Stack>
            {isMobileClient ? (
              <Button
                onClick={() => {
                  window.location.href = getDeepLink('forgot_password');
                }}
              >
                {pageContentTranslated.continue}
              </Button>
            ) : (
              <ButtonLink
                href={getHashRoute(LOGIN)}
                onClick={() =>
                  trackLink({
                    eventName: 'login_pressed',
                    linkPosition: 'body',
                    isThirdPartyLink: 'false',
                  })
                }
              >
                {pageContentTranslated.signIn}
              </ButtonLink>
            )}
          </Stack>
        ) : (
          <form onSubmit={onSubmit}>
            <FormStack>
              {isExpiredPasswordForm ? (
                <Alert tone="caution">
                  <Text>{pageContentTranslated.formPrimaryText}</Text>
                </Alert>
              ) : (
                <Text tone="secondary" size="small">
                  {pageContentTranslated.formPrimaryText}
                </Text>
              )}
              <EmailField useField={useField} formName={formName} />
              {error && (
                <ServerError
                  error={error}
                  ignoreErrors={['Email address is not registered.']}
                />
              )}
              <Button type="submit" tone="brandAccent" loading={loading}>
                {pageContentTranslated.next}
              </Button>
            </FormStack>
          </form>
        )}
        <TertiaryLinks>
          {!isMobileClient && (
            <>
              <Text>{pageContentTranslated.additionalHelpTertiaryLink}</Text>
              {experience === Experience.Candidate && (
                <Text>
                  {pageContentTranslated.advertiserCentreTertiaryLink}
                </Text>
              )}
            </>
          )}
        </TertiaryLinks>
      </FormCard>
    </Stack>
  );
};

const ForgotPasswordForm = () => {
  const { emailAddress } = useLoginPageContext();
  const { language } = useAppContext();

  return (
    <FormProvider initialValues={{ emailAddress }} language={language}>
      {({ handleSubmit }) => <Form handleSubmit={handleSubmit} />}
    </FormProvider>
  );
};

export default ForgotPasswordForm;
